import React, { useState } from 'react';
import { useGetOrderDetailsQuery } from '../../../redux/apis/endpoints/orderApi';
import LoadingSpinner from '../../ui/LoadingSpinner';
import { TruckIcon, BuildingStorefrontIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

const OrderContextSelector = ({ orderId, recipientId, onSendContext }) => {
  const [selectedFgIds, setSelectedFgIds] = useState([]);
  const [isSending, setIsSending] = useState(false);
  
  const { data: orderDetails, isLoading, error } = useGetOrderDetailsQuery(
    orderId,
    { skip: !orderId }
  );
  
  if (!orderId) {
    return null;
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-1 h-16">
        <LoadingSpinner size="small" />
      </div>
    );
  }
  
  if (error || !orderDetails) {
    return (
      <div className="text-xs text-red-500 p-1">
        Could not load order details
      </div>
    );
  }
  
  const { fulfillmentGroups = [] } = orderDetails;
  
  if (fulfillmentGroups.length === 0) {
    return (
      <div className="text-xs text-gray-500 p-1">
        No fulfillment groups found for this order
      </div>
    );
  }
  
  const handleCheckboxChange = (fgId) => {
    setSelectedFgIds(prev => {
      if (prev.includes(fgId)) {
        return prev.filter(id => id !== fgId);
      } else {
        return [...prev, fgId];
      }
    });
  };
  
  const handleSendSelected = async () => {
    if (selectedFgIds.length === 0 || isSending) {
      return;
    }
    
    setIsSending(true);
    
    try {
      // Send each context sequentially
      for (const fgId of selectedFgIds) {
        if (onSendContext) {
          await onSendContext(fgId);
        }
      }
      
      // Clear selections after all messages are sent
      setSelectedFgIds([]);
    } catch (error) {
      console.error('Error sending context messages:', error);
    } finally {
      setIsSending(false);
    }
  };
  
  // Helper function to get status text
  const getStatusText = (status) => {
    return status?.replace(/_/g, ' ') || 'Unknown status';
  };
  
  // Helper function to get status color
  const getStatusColor = (status) => {
    const statusLower = status?.toLowerCase() || '';
    
    if (statusLower.includes('completed') || statusLower.includes('delivered')) {
      return 'text-green-700';
    } else if (statusLower.includes('shipped') || statusLower.includes('transit')) {
      return 'text-blue-700';
    } else if (statusLower.includes('pending') || statusLower.includes('ready')) {
      return 'text-yellow-700';
    } else if (statusLower.includes('cancel')) {
      return 'text-red-700';
    } else if (statusLower.includes('return')) {
      return 'text-orange-700';
    }
    
    return 'text-gray-700';
  };
  
  return (
    <div className="border-t border-gray-200 py-1 px-2 bg-gray-50">
      <div className="mb-1 flex justify-between items-center">
        <h4 className="text-sm font-medium text-gray-700">Order Context</h4>
        <button
          onClick={handleSendSelected}
          disabled={selectedFgIds.length === 0 || isSending}
          className={`text-sm px-3 py-1 rounded flex items-center min-w-[100px] justify-center ${
            selectedFgIds.length > 0 && !isSending
              ? 'bg-brand-teal text-white hover:bg-brand-teal-dark'
              : 'bg-gray-200 text-gray-500 cursor-not-allowed'
          } transition-colors duration-150`}
        >
          {isSending && <LoadingSpinner size="tiny" className="mr-1" />}
          Send Selected ({selectedFgIds.length})
        </button>
      </div>
      
      {/* Horizontally scrollable container for fulfillment groups */}
      <div className="flex overflow-x-auto pb-1 space-x-2 scrollbar-thin scrollbar-thumb-gray-300 -mx-2 px-2">
        {fulfillmentGroups.map(fg => {
          const isSelected = selectedFgIds.includes(fg.id);
          const statusColor = getStatusColor(fg.status);
          const itemCount = fg.orderItems?.length || 0;
          
          return (
            <div 
              key={fg.id} 
              className={`flex-shrink-0 w-40 border rounded shadow-sm transition-all duration-150 ${
                isSelected 
                  ? 'border-brand-teal shadow bg-brand-teal bg-opacity-5' 
                  : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              {/* Fulfillment group header with checkbox */}
              <div className="px-2 py-1 border-b border-gray-200 bg-gray-50 rounded-t">
                <div className="flex items-center">
                  <div className="flex items-center flex-grow text-xs">
                    {fg.groupType === 'shipping' ? (
                      <TruckIcon className="h-3 w-3 text-gray-600 mr-1 flex-shrink-0" />
                    ) : (
                      <BuildingStorefrontIcon className="h-3 w-3 text-gray-600 mr-1 flex-shrink-0" />
                    )}
                    <span className="font-medium truncate">
                      {fg.groupType === 'shipping' ? 'Ship' : 'Pickup'} {itemCount} item{itemCount !== 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="flex-shrink-0 ml-1">
                    <label 
                      className="flex items-center cursor-pointer"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        type="checkbox"
                        id={`fg-${fg.id}`}
                        checked={isSelected}
                        onChange={() => handleCheckboxChange(fg.id)}
                        className="h-3 w-3 text-brand-teal focus:ring-brand-teal border-gray-300 rounded sr-only"
                      />
                      <div className={`w-4 h-4 rounded border flex items-center justify-center ${
                        isSelected 
                          ? 'bg-brand-teal border-brand-teal' 
                          : 'border-gray-300 bg-white'
                      }`}>
                        {isSelected && <CheckCircleIcon className="w-3 h-3 text-white" />}
                      </div>
                    </label>
                  </div>
                </div>
                
                <div className={`text-xs ${statusColor}`}>
                  {getStatusText(fg.status)}
                </div>
              </div>
              
              {/* Fulfillment group items/products */}
              <div className="p-1 bg-white">
                {/* Horizontally scrollable product images */}
                {fg.orderItems && itemCount > 0 ? (
                  <div className="flex overflow-x-auto space-x-1 pb-1">
                    {fg.orderItems.map((item, idx) => {
                      const product = item.product;
                      const imageUrl = product?.photos?.[0]?.url || 'https://via.placeholder.com/40';
                      
                      return (
                        <div 
                          key={`${fg.id}-item-${idx}`} 
                          className="flex-shrink-0 w-12 h-12 bg-gray-100 border border-gray-300 rounded overflow-hidden"
                          title={product?.name || 'Product'}
                        >
                          <img 
                            src={imageUrl} 
                            alt={product?.name || 'Item'} 
                            className="h-full w-full object-cover"
                          />
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 h-12 flex items-center justify-center">
                    No items
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default OrderContextSelector; 